import { Rng } from '../core/rng';
import { clamp, Vec3 } from '../core/math';
import { MASTS, cannonMount, deckY, halfBreadthXY, keelY, mastXAt, sheerY, sternX } from '../data/ship';
import { AMMO, type AmmoId } from '../data/classes';
import { DamageModel, sectionAt } from './damage';
import { walkHeight } from './crew';
import type { SimEvents } from './events';
import type { WaveField } from './waves';
import type { Wind } from './wind';
import type { Vessel } from './vessel';
import type { SailState } from './sails';

// Shot in flight and what it does when it arrives. Hits are resolved against
// the target's real geometry in design space: masts, canvas and rigging
// aloft; the bulwarks and hull planking; then whatever lies along the ball's
// path inside - men and guns in the waist, cargo and the magazine in the hold -
// until it runs out of energy or punches out through the far side.

export interface Projectile {
  pos: Vec3;
  vel: Vec3;
  ammo: AmmoId;
  /** Shot weight relative to a 12-pounder. */
  weight: number;
  owner: number;
  age: number;
  bounces: number;
  alive: boolean;
  /** Vessel the ball is currently inside, or -1. */
  inside: number;
  lastSail: SailState | null;
}

const BALL_R = 0.06;

export class Ballistics {
  readonly projectiles: Projectile[] = [];
  private readonly rng: Rng;

  constructor(seed: number) {
    this.rng = new Rng(seed);
  }

  launch(owner: number, pos: Vec3, vel: Vec3, ammo: AmmoId, weight: number): void {
    if (this.projectiles.length > 600) return;
    this.projectiles.push({ pos: pos.clone(), vel: vel.clone(), ammo, weight, owner, age: 0, bounces: 0, alive: true, inside: -1, lastSail: null });
  }

  update(dt: number, vessels: readonly Vessel[], waves: WaveField, wind: Wind, events: SimEvents): void {
    const prev = tmpP;
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const b = this.projectiles[i]!;
      b.age += dt;
      prev.copy(b.pos);
      const ammo = AMMO[b.ammo];
      // Drag against the air, which is moving with the wind.
      const ax = b.vel.x - wind.dirX * wind.speed, az = b.vel.z - wind.dirZ * wind.speed;
      const sp = Math.hypot(ax, b.vel.y, az);
      const k = ammo.drag / Math.cbrt(b.weight) * sp * dt;
      b.vel.x -= ax * k;
      b.vel.y -= b.vel.y * k + 9.81 * dt;
      b.vel.z -= az * k;
      b.pos.addScaled(b.vel, dt);

      for (const v of vessels) {
        if (!b.alive) break;
        if (v.state === 'sunk' || (v.id === b.owner && b.age < 0.5)) continue;
        if (!nearVessel(v, prev, b.pos)) continue;
        this.resolve(b, v, prev, dt, events);
      }
      if (!b.alive) {
        this.projectiles.splice(i, 1);
        continue;
      }

      const h = waves.heightAt(b.pos.x, b.pos.z, true);
      if (b.pos.y <= h) {
        const horiz = Math.hypot(b.vel.x, b.vel.z);
        const shallow = -b.vel.y < horiz * 0.1;
        events.push({ type: 'splash', x: b.pos.x, y: h, z: b.pos.z, size: (b.bounces === 0 ? 1 : 0.6) * Math.cbrt(b.weight) * (ammo.count > 1 ? 0.35 : 1), kind: 'ball' });
        if (ammo.explosive) {
          events.push({ type: 'explosion', x: b.pos.x, y: h, z: b.pos.z, size: 0.6, vessel: -1 });
          this.projectiles.splice(i, 1);
          continue;
        }
        if (shallow && horiz > 120 && b.bounces < 3 && ammo.count === 1 && this.rng.chance(0.7)) {
          // Round shot skips off the water at low angles.
          b.bounces++;
          b.pos.y = h + 0.05;
          b.vel.y = Math.abs(b.vel.y) * 0.45 + 2;
          b.vel.x *= 0.62;
          b.vel.z *= 0.62;
        } else {
          this.projectiles.splice(i, 1);
          continue;
        }
      }
      if (b.age > 14) this.projectiles.splice(i, 1);
    }
  }

  /** Walk the segment through a vessel's design space and apply hits. */
  private resolve(b: Projectile, v: Vessel, prevW: Vec3, dt: number, events: SimEvents): void {
    const ship = v.ship;
    const A = ship.localToDesign(ship.worldToLocal(prevW, tmpA), tmpA);
    const B = ship.localToDesign(ship.worldToLocal(b.pos, tmpB), tmpB);
    const len = A.distanceTo(B);
    const n = Math.max(1, Math.ceil(len / 0.3));
    const ammo = AMMO[b.ammo];
    const dir = tmpD.copy(B).sub(A).normalize();
    const dmgScale = () => {
      const speed = b.vel.length();
      return b.weight * (speed / 390) * (speed / 390);
    };
    let wasInside = b.inside === v.id;
    let deckSegStart: Vec3 | null = null;
    const P = tmpC;
    for (let s = 1; s <= n; s++) {
      P.copy(A).lerp(B, s / n);
      const speed = b.vel.length();
      if (speed < 40) {
        b.alive = false;
        return;
      }
      const kin = dmgScale();

      // --- aloft: masts
      for (const m of MASTS) {
        const md = v.damage.masts[m.id];
        if (!md || md.lostLevel >= 3) continue;
        const r = mastHit(m, md.lostLevel, P);
        if (r) {
          const amount = 18 * kin * (ammo.rig + 0.4) * (ammo.count > 1 ? 0.3 : 1);
          const wp = ship.designToWorld(tmpE.copy(P), tmpE);
          events.push({ type: 'impact', x: wp.x, y: wp.y, z: wp.z, what: 'mast', energy: kin, vessel: v.id, nx: -dir.x, ny: 0, nz: -dir.z });
          v.onMastHit(m.id, amount, P.y, Math.sign(dir.z) || 1, wp, events);
          if (ammo.explosive) v.explode(P.x, P.y, P.z, 0.8, events);
          if (ammo.count === 1) {
            b.alive = false;
            return;
          }
        }
      }
      // --- canvas
      if (P.y > deckY(P.x) + 1.5) {
        const sail = hitSail(v, P, b.lastSail);
        if (sail) {
          b.lastSail = sail;
          const tear = (ammo.id === 'chain' ? 1.1 : ammo.id === 'grape' ? 0.18 : 0.32) * Math.cbrt(b.weight);
          sail.cloth.tear(P.x, P.y, P.z, tear);
          const wp = ship.designToWorld(tmpE.copy(P), tmpE);
          events.push({ type: 'impact', x: wp.x, y: wp.y, z: wp.z, what: 'sail', energy: kin, vessel: v.id, nx: -dir.x, ny: 0, nz: -dir.z });
          b.vel.scale(ammo.id === 'chain' ? 0.9 : 0.98);
        }
        // Standing rigging: shrouds and stays around each mast.
        const rig = rigHit(v, P);
        if (rig && this.rng.chance(ammo.id === 'chain' ? 0.8 : 0.35)) {
          v.damage.damageRigging(rig, 0.05 * ammo.rig * Math.cbrt(b.weight));
          const wp = ship.designToWorld(tmpE.copy(P), tmpE);
          events.push({ type: 'impact', x: wp.x, y: wp.y, z: wp.z, what: 'rigging', energy: kin * 0.3, vessel: v.id, nx: -dir.x, ny: 0, nz: -dir.z });
        }
      }
      // --- rudder
      if (P.x < sternX(0.25) + 0.2 && P.x > sternX(0) - 1.6 && Math.abs(P.z) < 0.5 && P.y < 0.3 && P.y > keelY(P.x)) {
        v.damage.rudder = clamp(v.damage.rudder - 0.3 * kin * ammo.hull, 0, 1);
        if (v.damage.rudder <= 0.05) ship.rudderJam = ship.rudder;
        const wp = ship.designToWorld(tmpE.copy(P), tmpE);
        events.push({ type: 'impact', x: wp.x, y: wp.y, z: wp.z, what: 'rudder', energy: kin, vessel: v.id, nx: -dir.x, ny: 0, nz: -dir.z });
        b.alive = false;
        return;
      }

      // --- the shell of the hull
      const inside = DamageModel.inside(P.x, P.y, P.z) && P.y < sheerY(P.x) + 0.05;
      if (inside !== wasInside) {
        const entering = inside;
        const side = Math.sign(P.z) || 1;
        const sec = sectionAt(P.x);
        const aboveDeck = P.y > deckY(P.x) + 0.05;
        const punch = ammo.penetration * Math.cbrt(b.weight) * Math.pow(speed / 390, 1.2);
        const resist = (aboveDeck ? 0.16 : 0.42) * v.cls.hull;
        const hb = Math.max(0.2, halfBreadthXY(P.x, P.y));
        const wp = ship.designToWorld(tmpE.set(P.x, P.y, side * hb), tmpE);
        v.damage.damageHull(sec, side, 9 * ammo.hull * kin * (entering ? 1 : 0.6), events, wp);
        events.push({ type: 'impact', x: wp.x, y: wp.y, z: wp.z, what: aboveDeck ? 'rail' : 'hull', energy: kin, vessel: v.id, nx: side * (entering ? 1 : -1), ny: 0, nz: 0 });
        if (aboveDeck) v.damage.breachRail(P.x, side, 0.7 * Math.cbrt(b.weight) * ammo.hull);
        if (ammo.explosive && entering) {
          v.explode(P.x, P.y, P.z * 0.9, 1, events);
          b.alive = false;
          return;
        }
        if (punch > resist) {
          if (!aboveDeck && ammo.hull > 0.2) v.damage.addHole(P.x, P.y, side * hb, 0.075 * Math.cbrt(b.weight) * (entering ? 1 : 1.4));
          // A shower of oak splinters inboard: the real killer at sea.
          if (ammo.hull > 0.2) {
            const inboard = entering ? -side : side;
            v.crew.strike(P.x, P.y, P.z + inboard * 1.2, 3.2, 0.28 * ammo.hull * clamp(kin, 0.3, 1.5), v.hitCtx(events));
          }
          b.vel.scale(Math.sqrt(Math.max(0.05, 1 - resist / punch)));
        } else {
          // Lodged in the planking.
          b.alive = false;
          return;
        }
        wasInside = inside;
        b.inside = inside ? v.id : -1;
        if (!inside) deckSegStart = null;
      }
      if (inside) {
        const above = P.y > walkHeight(P.x, P.z) + 0.05;
        if (above) {
          deckSegStart ??= P.clone();
          // Guns in the path.
          v.battery.guns.forEach((g, gi) => {
            if (g.destroyed) return;
            const mnt = cannonMount(g.spec);
            if (Math.abs(mnt.x - P.x) < 0.7 && Math.abs(mnt.z - P.z) < 1.1 && Math.abs(mnt.y - P.y) < 0.8) {
              if (v.battery.damageGun(gi, 18 * kin * (ammo.hull + 0.3))) {
                const gw = ship.designToWorld(tmpE.set(mnt.x, mnt.y, mnt.z), tmpE);
                events.push({ type: 'impact', x: gw.x, y: gw.y, z: gw.z, what: 'gun', energy: kin, vessel: v.id, nx: -dir.x, ny: 0, nz: -dir.z });
              }
              v.crew.strike(mnt.x, mnt.y, mnt.z, 2.5, 0.3, v.hitCtx(events));
            }
          });
        } else {
          // Through the deck from above (plunging fire)?
          if (deckSegStart) {
            v.damage.deck[sectionAt(P.x)]! -= 6 * kin;
            v.damage.version++;
          }
          // Below: through stores and bulkheads, losing speed as it goes.
          b.vel.scale(Math.exp(-0.1 * 0.3));
          const sec = sectionAt(P.x);
          v.damage.cargo[sec] = Math.max(0, v.damage.cargo[sec]! - 0.02 * kin);
          const mag = v.damage.magazine;
          if (Math.abs(P.x - mag.x) < 1.6 && Math.abs(P.y - mag.y) < 1.2 && Math.abs(P.z) < 1.5) {
            mag.heat += ammo.explosive ? 2 : this.rng.chance(0.15) ? 0.6 : 0.05;
          }
          if (ammo.explosive) {
            v.explode(P.x, P.y, P.z, 1, events);
            b.alive = false;
            return;
          }
          // Men below the waist.
          if (this.rng.chance(0.05 * ammo.crew)) v.crew.reserve = Math.max(0, v.crew.reserve - 1);
        }
      }
    }
    // Men in the ball's path across the open deck.
    if (deckSegStart) {
      const end = wasInside ? P : B;
      v.crew.sweep(deckSegStart.x, deckSegStart.y, deckSegStart.z, end.x, end.y, end.z, ammo.count > 1 ? 0.45 : 0.35, clamp(0.7 * ammo.crew, 0, 1), v.hitCtx(events));
    }
    void dt;
  }
}

/** Cheap reject: does the segment pass within reach of the vessel? */
function nearVessel(v: Vessel, a: Vec3, b: Vec3): boolean {
  const c = v.ship.com;
  const r = 36 * Math.max(v.ship.sx, v.ship.sy) + 2;
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const L2 = dx * dx + dy * dy + dz * dz || 1;
  const t = clamp(((c.x - a.x) * dx + (c.y + 12 * v.ship.sy - a.y) * dy + (c.z - a.z) * dz) / L2, 0, 1);
  const px = a.x + dx * t - c.x, py = a.y + dy * t - (c.y + 12 * v.ship.sy), pz = a.z + dz * t - c.z;
  return px * px + py * py + pz * pz < r * r;
}

/** Distance test against the (standing parts of the) mast. */
function mastHit(m: (typeof MASTS)[number], lostLevel: number, p: Vec3): boolean {
  const segs: [number, number, number, number][] = [[m.deckY, m.lowerTop, 0, m.radius]];
  if (lostLevel < 2) segs.push([m.lowerTop - 2.4, m.topmastTop, 0.5, m.radius * 0.62]);
  if (lostLevel < 1 && m.topgallantTop) segs.push([m.topmastTop - 1.6, m.topgallantTop, 0.85, m.radius * 0.36]);
  for (const [y0, y1, off, r] of segs) {
    if (p.y < y0 || p.y > y1) continue;
    const mx = mastXAt(m, p.y) + off;
    if (Math.hypot(p.x - mx, p.z) < r + BALL_R) return true;
  }
  return false;
}

function hitSail(v: Vessel, p: Vec3, skip: SailState | null): SailState | null {
  for (const s of v.ship.sails.sails) {
    if (s === skip || s.lost || s.deploy < 0.1) continue;
    const box = v.sailBox(s);
    if (p.x < box[0]! - 0.4 || p.x > box[3]! + 0.4 || p.y < box[1]! - 0.4 || p.y > box[4]! + 0.4 || p.z < box[2]! - 0.4 || p.z > box[5]! + 0.4) continue;
    const c = s.cloth;
    for (let i = 0; i < c.count; i += 1) {
      const dx = c.pos[i * 3]! - p.x, dy = c.pos[i * 3 + 1]! - p.y, dz = c.pos[i * 3 + 2]! - p.z;
      if (dx * dx + dy * dy + dz * dz < 0.36) return s;
    }
  }
  return null;
}

/** Inside the fan of shrouds between the channel and the masthead? */
function rigHit(v: Vessel, p: Vec3): (typeof MASTS)[number]['id'] | null {
  for (const m of MASTS) {
    const md = v.damage.masts[m.id];
    if (!md || md.lostLevel >= 3) continue;
    if (Math.abs(p.x - (m.x - 0.8)) > 3) continue;
    const y0 = sheerY(m.x) - 0.3, y1 = m.lowerTop - 1.3;
    if (p.y < y0 || p.y > y1) continue;
    const t = (p.y - y0) / (y1 - y0);
    const zc = (halfBreadthXY(m.x, y0 - 0.2) + 0.5) * (1 - t) + 0.35 * t;
    if (Math.abs(Math.abs(p.z) - zc) < 0.3) return m.id;
  }
  return null;
}

const tmpA = new Vec3(), tmpB = new Vec3(), tmpC = new Vec3(), tmpD = new Vec3(), tmpE = new Vec3(), tmpP = new Vec3();
